/* ClarityNudge — global, appears on path preview pages after 20s dwell.
   Signed-in only (401 → never shows). Answers save to /clarity-score/checkin.
   24h snooze after answering or dismissing. */
import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { X, TrendingUp, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SNOOZE_KEY = 'lxt_clarity_nudge_ts';
const SNOOZE_MS = 24 * 60 * 60 * 1000;
const DWELL_MS = 20000;

const snoozed = () => {
  try { return Date.now() - (Number(localStorage.getItem(SNOOZE_KEY)) || 0) < SNOOZE_MS; } catch { return false; }
};
const snooze = () => { try { localStorage.setItem(SNOOZE_KEY, String(Date.now())); } catch {} };

const chipColor = (n) => {
  if (n <= 3) return 'hover:bg-rose-500 hover:border-rose-500';
  if (n <= 6) return 'hover:bg-amber-500 hover:border-amber-500';
  return 'hover:bg-emerald-500 hover:border-emerald-500';
};

export const ClarityNudge = () => {
  const { pathname } = useLocation();
  const [show, setShow] = useState(false);
  const [me, setMe] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  const onPreview = pathname.includes('/preview/');

  useEffect(() => {
    clearTimeout(timer.current);
    setShow(false);
    setResult(null);
    if (!onPreview || snoozed()) return;
    timer.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/clarity-score/me`, { withCredentials: true });
        setMe(data);
        setShow(true);
      } catch { /* anonymous — never nudge */ }
    }, DWELL_MS);
    return () => clearTimeout(timer.current);
  }, [pathname, onPreview]);

  if (!show) return null;

  const tap = async (score) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/clarity-score/checkin`, { score }, { withCredentials: true });
      setResult(data);
      snooze();
      setTimeout(() => setShow(false), 3500);
    } catch { setShow(false); } finally { setBusy(false); }
  };

  const dismiss = () => { snooze(); setShow(false); };

  return (
    <div
      className="fixed bottom-5 left-4 right-4 sm:left-6 sm:right-auto sm:w-[380px] z-[60] bg-white rounded-2xl border-2 border-teal-200 shadow-2xl p-4"
      style={{ animation: 'lxt-nudge-up 0.45s ease' }}
      data-testid="clarity-nudge"
    >
      <style>{`@keyframes lxt-nudge-up { 0% { opacity: 0; transform: translateY(24px); } 100% { opacity: 1; transform: translateY(0); } }`}</style>
      {!result ? (
        <>
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 text-teal-700 text-[10px] font-black uppercase tracking-widest">
              <TrendingUp size={13} /> 5-second check
            </div>
            <button type="button" onClick={dismiss} data-testid="clarity-nudge-close" className="text-gray-400 hover:text-gray-700 -mt-1 -mr-1 p-1">
              <X size={16} />
            </button>
          </div>
          <p className="font-extrabold text-gray-900 text-sm mt-1">
            {me?.before != null
              ? <>You started at {me.before}/10 — how clear are you <span className="text-teal-600">now</span>?</>
              : 'How clear are you about your career path right now?'}
          </p>
          <div className="flex flex-wrap gap-1.5 mt-2.5">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
              <button
                key={n} type="button" disabled={busy} onClick={() => tap(n)}
                data-testid={`clarity-nudge-${n}`}
                className={`w-8 h-8 rounded-lg border-2 border-gray-200 bg-white text-gray-700 font-extrabold text-xs transition-all hover:scale-110 hover:text-white disabled:opacity-50 ${chipColor(n)}`}
              >
                {n}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-gray-400 mt-2">1 = totally confused · 10 = crystal clear</p>
        </>
      ) : (
        <div className="flex items-center gap-3" data-testid="clarity-nudge-thanks">
          <CheckCircle2 size={28} className="text-emerald-600 shrink-0" />
          <div>
            <p className="font-extrabold text-gray-900 text-sm">
              {result.phase_saved === 'after' && result.after > result.before
                ? <>{result.before}/10 → {result.after}/10 · <span className="text-emerald-600">+{result.after - result.before} clearer</span> 🎉</>
                : 'Saved — thank you!'}
            </p>
            <p className="text-[11px] text-gray-500">Your check-in lives on your dashboard.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClarityNudge;
